'use client';

import { QueryKeys } from '@/constants/query-keys';
import { AnalyticsEvent, AnalyticsLinkAction, AnalyticsPageCategories } from '@/enums/analytics-type';
import { useAnalyticsDataLayer, useDataAnalytics, useUpdateAnalyticsDataLayer } from '@/hooks/analytics/analytics';
import { useAnalyticsQueue, useAutoProcessAnalyticsQueue } from '@/hooks/analytics/analytics-queue';
import { useIsMobile } from '@/hooks/is-mobile';
import { UpdateSubscriptionWithAnalyticsType } from '@/hooks/profile/email-subscriptions';
import { UpdateFavoriteTheaterWithAnalyticsType } from '@/hooks/profile/favorite-theaters';
import { UpdateShippingAddressWithAnalyticsType } from '@/hooks/profile/shipping-address';
import { UpdateWatchlistWithAnalyticsType } from '@/hooks/profile/watch-list';
import { useFeatureFlags } from '@/hooks/state/feature-flags';
import { AnalyticsDataLayer } from '@/types/analytics';
import { formatTheaterAddress } from '@/utils/address-helper';
import { globalWindow } from '@/utils/global-window';
import { Theater } from '@cinemark/content-api-client';
import { PagedListOfShippingAddress, Profile, ShippingAddressData } from '@cinemark/profile-api-client';
import { useQueryClient } from '@tanstack/react-query';
import Script from 'next/script';
import { useCallback, useEffect, useRef } from 'react';

type AnalyticsProviderProps = {
  analyticsScriptSrc: string;
};

export const AnalyticsProvider = ({ analyticsScriptSrc }: AnalyticsProviderProps): React.JSX.Element => {
  const initialized = useRef(false);
  const queryClient = useQueryClient();
  const { isMobile } = useIsMobile();
  const { data: featureFlags } = useFeatureFlags();
  const { data: analyticsDataLayer } = useAnalyticsDataLayer();
  const { mutateAsync: updateAnalyticsDataLayer } = useUpdateAnalyticsDataLayer();
  const { trackEvent } = useDataAnalytics();
  const isAnalyticsEnabled = !!featureFlags?.enableTealium;

  useAnalyticsQueue();
  useAutoProcessAnalyticsQueue();

  const handleScriptReady = (): void => {
    const isReady = !!globalWindow?.utag && isAnalyticsEnabled;
    queryClient.setQueryData(QueryKeys.global.analyticsReady, isReady);
  };

  const initializeAnalyticsData = useCallback(async (): Promise<void> => {
    await updateAnalyticsDataLayer({
      customerId: '0',
      customerType: 'New Member',
      memberType: 'Guest',
      newReturningUser: 'New User',
      orderPlatform: isMobile ? 'Mobile' : 'Web',
    });
  }, [isMobile, updateAnalyticsDataLayer]);

  // though this is nullable, ideally we should only call this when we have a profile
  // if we don't we should call initializeAnalyticsData instead
  const updateProfileDataInAnalytics = useCallback(async (profile?: Profile): Promise<void> => {
    await updateAnalyticsDataLayer({
      customerEmail: profile?.email,
      customerFirstName: profile?.firstName,
      customerId: profile?.memberId?.toString(),
      customerLastName: profile?.lastName,
      customerPostalCode: profile?.postalCode,
      customerType: 'Returning Member',
      memberType: profile?.subscription?.name || 'Movie Fan Member',
    });
  }, [updateAnalyticsDataLayer]);

  const updateProfileShippingAddressDataInAnalytics = useCallback(async (shippingAddressData?: ShippingAddressData): Promise<void> => {
    await updateAnalyticsDataLayer({
      customerCity: shippingAddressData?.city,
      customerState: shippingAddressData?.state,
    });
  }, [updateAnalyticsDataLayer]);

  const updateTheaterDataInAnalytics = useCallback(async (theater: Theater): Promise<void> => {
    await updateAnalyticsDataLayer({
      theatreAddress: formatTheaterAddress(theater),
      theatreId: theater.theaterId?.toString(),
      theatreName: theater.theaterName,
    });
  }, [updateAnalyticsDataLayer]);

  useEffect(() => {
    const queryCache = queryClient.getQueryCache();
    const mutationCache = queryClient.getMutationCache();

    const unsubscribeQuery = queryCache.subscribe(async (event) => {
      if ((event?.type === 'added' || event?.type === 'removed' || event?.type === 'updated') && event.query.state.status === 'success') {
        const queryKey = event.query.queryKey;
        const data = event.query.state.data;

        switch (queryKey[0]) {
          case QueryKeys.user.profile[0]:
            if (event.type === 'removed') {
              await initializeAnalyticsData();
            } else {
              await updateProfileDataInAnalytics(data as Profile);
            }

            break;
          case QueryKeys.user.shippingAddresses[0]:
            if (event.type === 'removed') {
              await initializeAnalyticsData();
            } else {
              const firstShippingAddress = (data as PagedListOfShippingAddress)?.data[0];
              await updateProfileShippingAddressDataInAnalytics(firstShippingAddress);
            }

            break;
          case QueryKeys.global.theaterContext[0]:
            const theater = data as Theater;
            // Synchronously update the React Query data layer cache so that
            // PageViewTracker reads the theater fields in the same render cycle
            // (before the awaited mutation below resolves).
            queryClient.setQueryData<AnalyticsDataLayer>(QueryKeys.user.analytics, old => ({
              ...old,
              theatreAddress: formatTheaterAddress(theater),
              theatreId: theater.theaterId?.toString(),
              theatreName: theater.theaterName,
            }));

            // Also run through updateAnalyticsDataLayer so the DATA_LAYER event
            // is enqueued and utag.data is updated for subsequent Tealium events.
            await updateTheaterDataInAnalytics(theater);

            break;
          default:
            break;
        }
      }
    });

    const unsubscribeMutation = mutationCache.subscribe(async (event) => {
      if (event?.type === 'updated' &&
          event.mutation.state.status === 'success' &&
          event.mutation.state.context &&
          typeof event.mutation.state.context === 'object') {
        const { analyticsEvent, queryKey } = event.mutation.state.context as { analyticsEvent?: AnalyticsEvent; queryKey: readonly string[]; };
        const data = event.mutation.state.data;

        if (!queryKey && !analyticsEvent) return;

        switch (queryKey[0]) {
          case QueryKeys.user.profile[0]:
            await updateProfileDataInAnalytics(data as Profile);

            if (analyticsEvent) {
              trackEvent(analyticsEvent);
            }

            break;
          case QueryKeys.user.emailSubscriptions[0]:
            const { subscribed } = event.mutation.state.context as UpdateSubscriptionWithAnalyticsType;

            if (analyticsEvent && subscribed) {
              trackEvent(analyticsEvent);
            }

            break;
          case QueryKeys.user.favoriteTheaters[0]:
            const { isFavorite: isFavoriteTheater, theaterId, theaterName } = event.mutation.state.context as UpdateFavoriteTheaterWithAnalyticsType;

            if (analyticsEvent) {
              trackEvent(analyticsEvent, {
                linkAction: AnalyticsLinkAction.CLICK,
                linkCategory: 'Favorite Theater',
                linkName: isFavoriteTheater ? 'Add Favorite Theater' : 'Remove Favorite Theater',
                productId: theaterId,
                productName: theaterName,
              });
            }

            break;
          case QueryKeys.user.shippingAddresses[0]:
            const { addressData } = event.mutation.state.context as UpdateShippingAddressWithAnalyticsType;
            await updateProfileShippingAddressDataInAnalytics(addressData);

            if (analyticsEvent) {
              trackEvent(analyticsEvent);
            }

            break;
          case QueryKeys.user.watchList[0]:
            const {
              isFavorite: isFavoriteMovie,
              linkCategory,
              movieId,
              movieTitle,
            } = event.mutation.state.context as UpdateWatchlistWithAnalyticsType;

            if (analyticsEvent) {
              trackEvent(analyticsEvent, {
                linkAction: AnalyticsLinkAction.CLICK,
                linkCategory: linkCategory ?? AnalyticsPageCategories.WATCH_LIST,
                linkName: isFavoriteMovie ? 'Add to Watch List' : 'Remove from Watch List',
                productId: movieId,
                productName: movieTitle,
              });
            }

            break;
          default:
            break;
        }
      }
    });

    return (): void => {
      unsubscribeQuery();
      unsubscribeMutation();
    };
  }, [
    initializeAnalyticsData,
    queryClient,
    trackEvent,
    updateProfileDataInAnalytics,
    updateProfileShippingAddressDataInAnalytics,
    updateTheaterDataInAnalytics,
  ]);

  useEffect(() => {
    if (initialized.current) {
      return;
    }

    initializeAnalyticsData();
    initialized.current = true;
  }, [analyticsDataLayer, initializeAnalyticsData]);

  return (
    <>
      {isAnalyticsEnabled && analyticsScriptSrc && <Script src={analyticsScriptSrc}
        strategy="afterInteractive"
        onReady={handleScriptReady}
      />}
    </>
  );
};
